import React, {useCallback, useEffect, useState} from "react";
import {Col, Container, Modal, Row} from "react-bootstrap";
import {
  getClassroomObservationByStudent,
  getClassroomObservationsForClass,
  upsertClassroomObservation,
} from "../services/APIClient";
import ExpandedHandoverView from "./ExpandedHandoverView";
import HandoverClassTable from "./HandoverClassTable";
import PrintClassroomObservations from "./PrintClassroomObservations";
import "./ClassOverview.css";
import "./JiSiReportsOverview.css";

function ClassroomObservationsOverview({
  classDetails,
  students,
  studentsReadingScore,
  teachers,
  teacherid,
  additionalRoles,
  academicYear,
  gradeName,
}) {
  const [showExpandedModal, setShowExpandedModal] = useState(false);
  const [showAtRiskOnly, setShowAtRiskOnly] = useState(false);
  const [showSortedData, setShowSortedData] = useState(false);
  const [observationsByStudent, setObservationsByStudent] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setObservationsByStudent(null);
    setLoadError("");

    getClassroomObservationsForClass(classDetails.id, academicYear, 3)
      .then((observations) => {
        if (!cancelled) {
          setObservationsByStudent(
            new Map(observations.map((observation) => [observation.studentid, observation]))
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError("Classroom observations could not be loaded. Please try again.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [academicYear, classDetails.id]);

  const getReport = useCallback(
    (studentId) => {
      if (observationsByStudent) {
        return Promise.resolve(observationsByStudent.get(studentId) || null);
      }
      return getClassroomObservationByStudent(
        classDetails.id,
        studentId,
        academicYear,
        3
      );
    },
    [academicYear, classDetails.id, observationsByStudent]
  );

  const upsertReport = useCallback(
    async (reportData) => {
      const savedObservation = await upsertClassroomObservation(
        classDetails.id,
        reportData.studentid,
        academicYear,
        reportData,
        3
      );
      setObservationsByStudent((current) => {
        const next = new Map(current || []);
        next.set(savedObservation.studentid, savedObservation);
        return next;
      });
      return savedObservation;
    },
    [academicYear, classDetails.id]
  );

  if (loadError) {
    return <div className="alert alert-danger">{loadError}</div>;
  }

  if (!observationsByStudent) {
    return <div className="text-center py-4">Loading classroom observations...</div>;
  }

  return (
    <div className="class-overview-container">
      <Container className="container-shape">
        <Row className="page-description-bar">
          <Col>
            <h2 className="page-description-header">
              {classDetails.name} {gradeName} Observations
            </h2>
            <p>Use edit buttons to add details for each student</p>
          </Col>
          <Col className="col-auto d-flex justify-content-end align-items-start">
            <PrintClassroomObservations
              classDetails={classDetails}
              students={students}
              studentsReadingScore={studentsReadingScore}
              teachers={teachers}
              academicYear={academicYear}
              gradeName={gradeName}
              getReport={getReport}
            />
          </Col>
        </Row>
        <hr />
        <Row>
          <Col className="d-flex justify-content-end">
            <button
              type="button"
              className="recommendations-button"
              onClick={() => setShowExpandedModal(true)}
            >
              Expanded View
            </button>
          </Col>
        </Row>
        <HandoverClassTable
          getReport={getReport}
          upsertReport={upsertReport}
          students={students}
          classDetails={classDetails}
          teachers={teachers}
          teacherid={teacherid}
          studentsReadingScore={studentsReadingScore}
          showAtRiskOnly={showAtRiskOnly}
          setShowAtRiskOnly={setShowAtRiskOnly}
          setShowSortedData={setShowSortedData}
          additionalRoles={additionalRoles}
          noteType="ClassroomObservation"
          ealManagedByReport={true}
        />
        <Modal
          show={showExpandedModal}
          onHide={() => setShowExpandedModal(false)}
          size="xl"
          centered
          className="custom-modal-width jisi-expandable-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Expanded View of {gradeName} Observations - {classDetails.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ExpandedHandoverView
              students={students}
              classid={classDetails.id}
              teachers={teachers}
              showAtRiskOnly={showAtRiskOnly}
              setShowAtRiskOnly={setShowAtRiskOnly}
              studentsReadingScore={studentsReadingScore}
              onSortData={setShowSortedData}
              showSortedData={showSortedData}
              getReport={getReport}
              noteType="ClassroomObservation"
            />
          </Modal.Body>
          <Modal.Footer>
            <button
              className="blue-action-button"
              onClick={() => setShowExpandedModal(false)}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default ClassroomObservationsOverview;
